/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { animate, AnimationTreeItemExpandName, baseStyles, createId, event, i18n, I18nService, property, querySlot, querySlotAll, reverseAnimation, } from '@cds/core/internal';
import { html, LitElement } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import styles from './tree-item.element.scss';
/**
 * Tree view is a hierarchical component that gives users access to a hierarchical set of objects displayed in a the parent-child relationship.
 *
 * ```typescript
 * import '@cds/core/tree-view/register.js';
 * ```
 *
 * ```html
 *  <cds-tree>
 *    <cds-tree-item>1</cds-tree-item>
 *    <cds-tree-item>2</cds-tree-item>
 *    <cds-tree-item>3</cds-tree-item>
 *  </cds-tree>
 * ```
 *
 * @element cds-tree-item
 * @slot - Content slot for inside the tree item
 * @slot expand-collapse-icon
 * @event expandedChange - notify when the user has clicked the expand / collapse button
 * @event selectedChange - notify when the user has clicked the tree item
 * @cssprop --animation-duration
 * @cssprop --animation-easing
 * @cssprop --background
 * @cssprop --color
 * @cssprop --focus-width
 * @cssprop --font-size
 * @cssprop --font-weight
 */
let CdsTreeItem = class CdsTreeItem extends LitElement {
    constructor() {
        super(...arguments);
        this.i18n = I18nService.keys.treeview;
        this.cdsMotion = 'on';
        this.multiSelect = false;
        this.disabled = false;
        this.expanded = false;
        /**
         * @description
         *
         * Expandable is used (see line 124) to automatically show the cds-button-expand element when a tree-item detects that
         * there are children. However, when doing investigation into why styles were not applied for tree-items controlled
         * via *ngIf (or any conditional DOM and code that will put tree-item elements there at some unknown point in the
         * future) it was re-discovered as a public property without a demo for manual usage.
         */
        this.expandable = false;
        this.indeterminate = false;
        this.loading = false;
        this.selected = false;
    }
    connectedCallback() {
        super.connectedCallback();
        this.tabIndex = -1;
        this.role = 'treeitem';
        if (!this.id) {
            this.id = createId();
        }
    }
    updated(props) {
        super.updated(props);
        if (this.treeItemChildren.length > 0) {
            this.expandable = true;
        }
        if (this.expandable) {
            this.ariaExpanded = this.expanded ? 'true' : 'false';
        }
        else {
            this.ariaExpanded = null;
        }
        if (this.multiSelect) {
            this.ariaSelected = this.selected ? 'true' : 'false';
        }
        else {
            this.ariaSelected = null;
        }
        this.ariaDisabled = this.disabled ? 'true' : 'false';
    }
    toggleExpanded() {
        this.expandedChange.emit(!this.expanded);
    }
    toggleSelected() {
        this.selectedChange.emit(!this.selected);
        if (this.anchorLink) {
            this.anchorLink.click();
        }
    }
    onSlotChange() {
        this.treeItemChildren.forEach(i => {
            if (!i.getAttribute('slot')) {
                i.setAttribute('slot', createId());
            }
        });
        this.requestUpdate();
    }
    render() {
        return html `
      <div class="private-host" cds-layout="p-l:md">
        <div cds-layout="horizontal align:vertical-center ${this.multiSelect ? 'gap:xs' : 'gap:xxs'}">
          <div class="lhs-container">${this.treeItemLeftHandSideTemplate}</div>
          <div
            class="item-content"
            cds-layout="horizontal p:xxs align:vertical-center align:stretch"
            @click="${() => this.toggleSelected()}"
          >
            <span cds-layout="horizontal align:vertical-center gap:sm" cds-text="lhe">
              ${this.multiSelect
            ? html `
                    <cds-internal-visual-checkbox
                      part="checkbox"
                      .disabled="${this.disabled}"
                      .indeterminate="${this.indeterminate}"
                      .selected="${this.selected}"
                    ></cds-internal-visual-checkbox>
                  `
            : html ``}
              <slot @slotchange=${this.onSlotChange}></slot>
            </span>
          </div>
        </div>
        <div class="item-children" role=${ifDefined(this.expanded ? 'group' : undefined)} ?hidden="${!this.expanded}">
          ${Array.from(this.treeItemChildren).map((i) => html `<slot name="${i.getAttribute('slot')}"></slot>`)}
        </div>
      </div>
    `;
    }
    /**
     * This container contains one of the following depending on the state of the tree item:
     *   - caret if the tree item is expandable
     *   - progress circle if the tree item is loading its children (via an async call, etc)
     *   - nothing if the tree item is an end node
     *
     * The container with a fixed width ensures the same alignment whether or not it contains
     * a child elemeent to render.
     */
    /** @private */
    get treeItemLeftHandSideTemplate() {
        if (this.loading) {
            return html ` <cds-progress-circle size="xs" aria-label="${this.i18n.loading}"></cds-progress-circle> `;
        }
        else if (this.expandable) {
            return html `
        <div @click="${() => this.toggleExpanded()}">
          <cds-button-expand
            .expanded=${this.expanded}
            readonly
            action="vertical"
            part="expand-collapse-icon"
          ></cds-button-expand>
        </div>
      `;
        }
        else {
            return html ``;
        }
    }
    static get styles() {
        return [baseStyles, styles];
    }
};
__decorate([
    i18n()
], CdsTreeItem.prototype, "i18n", void 0);
__decorate([
    property({ type: String })
], CdsTreeItem.prototype, "cdsMotion", void 0);
__decorate([
    event()
], CdsTreeItem.prototype, "cdsMotionChange", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'multi-select' })
], CdsTreeItem.prototype, "multiSelect", void 0);
__decorate([
    property({ type: Boolean })
], CdsTreeItem.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], CdsTreeItem.prototype, "expanded", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], CdsTreeItem.prototype, "expandable", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], CdsTreeItem.prototype, "indeterminate", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], CdsTreeItem.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], CdsTreeItem.prototype, "selected", void 0);
__decorate([
    querySlotAll(':scope > cds-tree-item')
], CdsTreeItem.prototype, "treeItemChildren", void 0);
__decorate([
    querySlot(':scope > a')
], CdsTreeItem.prototype, "anchorLink", void 0);
__decorate([
    event()
], CdsTreeItem.prototype, "expandedChange", void 0);
__decorate([
    event()
], CdsTreeItem.prototype, "selectedChange", void 0);
CdsTreeItem = __decorate([
    animate({
        expanded: {
            true: AnimationTreeItemExpandName,
            false: reverseAnimation(AnimationTreeItemExpandName),
        },
    })
], CdsTreeItem);
export { CdsTreeItem };
